import Axios from 'axios';

/**
 * Password access for the phone's Missed Calls upload page (server docs/MISSED_CALLS_PLAN.md §6.10).
 *
 * Whoever holds the office iPhone has no Universe login, so this page asks for one shared password and then
 * "who is uploading?" — a name picked from the same 담당자 목록 the dashboard menus use. The server hands back a
 * token that opens the six upload endpoints and nothing else; it is not an admin session anywhere.
 *
 * The token lives in localStorage so the phone is not asked again every day. It is cleared on any 401 — which is
 * also what happens the moment the password is rotated on the server, since every token carries a fingerprint of
 * the password that minted it.
 */
const BASE = `${process.env.REACT_APP_URL}/api/v1/missed-calls/upload`;

const TOKEN_KEY = 'missedCallsUploadToken';
const ADMIN_KEY = 'missedCallsUploadAdmin';
const ACTIVE_KEY = 'missedCallsUploadActiveAt';

/**
 * Admins share the office iPhone, so "who is uploading?" goes stale in a way the password does not: leave the
 * phone for half an hour and the next person to pick it up would otherwise upload under the last person's name —
 * and that name is what `missed_call_events` records. After this much idle time the page asks the name again
 * (never the password, which stays remembered).
 */
export const IDENTITY_MAX_IDLE_MS = 30 * 60 * 1000;

/**
 * Blocked site data (Safari's "Block All Cookies") makes localStorage THROW rather than return null, and a phone
 * in that state must still be able to upload. So the module keeps its own copy: memory is the source of truth for
 * this page load, storage is the durable copy that lets the phone skip the password next time.
 *
 * Without the memory copy the token would be written, swallowed, and read back as null — the page would then send
 * no credential at all, take a 401, and land on the /signin screen nobody on that phone can use.
 */
const mem = new Map();

const read = (key) => {
    if (mem.has(key)) return mem.get(key);
    try { return localStorage.getItem(key); } catch { return null; }
};
const write = (key, value) => {
    if (value === null) mem.delete(key); else mem.set(key, value);
    try {
        if (value === null) localStorage.removeItem(key);
        else localStorage.setItem(key, value);
    } catch { /* memory carries this page load; the phone just re-enters the password next time */ }
};

export const getUploadToken = () => read(TOKEN_KEY);

export const getUploadAdmin = () => {
    const raw = read(ADMIN_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
};

export const setUploadSession = (token, admin) => {
    write(TOKEN_KEY, token || null);
    write(ADMIN_KEY, admin ? JSON.stringify(admin) : null);
    write(ACTIVE_KEY, admin ? String(Date.now()) : null);
};

/** Someone is using the page right now. Written to storage, so it survives the tab being discarded and reopened. */
export const touchActivity = () => write(ACTIVE_KEY, String(Date.now()));

/**
 * Has the phone been left alone long enough that we should not assume the same person is holding it?
 * Unreadable or missing storage counts as stale — asking the name again is the safe direction to fail.
 */
export const identityIsStale = () => {
    const at = Number.parseInt(read(ACTIVE_KEY), 10);
    if (!at) return true;
    return Date.now() - at > IDENTITY_MAX_IDLE_MS;
};

export const clearUploadSession = () => setUploadSession(null, null);

/**
 * A 401 can land in any of the page's calls — including the 3-second poll, which swallows its errors. Without a
 * way back, a rotated password leaves the phone spinning on "읽는 중" forever. The gate subscribes here so it can
 * drop straight back to the password screen wherever the failure happened.
 */
let sessionLostHandler = null;
export const onSessionLost = (handler) => {
    sessionLostHandler = handler;
    return () => { if (sessionLostHandler === handler) sessionLostHandler = null; };
};
export const notifySessionLost = () => { if (sessionLostHandler) sessionLostHandler(); };

const authHeader = (token) => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });

/** The password → { token, admins, limited }. The roster only comes back once the password is right. */
export const requestAccess = (password) =>
    Axios.post(`${BASE}/access`, { password }, { headers: { 'Content-Type': 'application/json' } })
        .then((r) => r.data);

/**
 * Who is holding the phone → the token the upload endpoints take. Stored on success. Takes the token from the
 * password step, or the current one when the page is only re-asking the name after an idle spell.
 */
export const chooseIdentity = (token, admin) =>
    Axios.post(`${BASE}/identity`, { admin_uuid: admin.uuid }, { headers: authHeader(token) })
        .then((r) => {
            setUploadSession(r.data.token, r.data.admin);
            return r.data;
        });

/**
 * Still signed in? Refreshes the stored name, or clears the session on a 401 so the page asks again. The timeout
 * matters: this runs behind a full-screen spinner, and a phone on a flaky connection would otherwise sit there.
 */
export const confirmSession = () => {
    const token = getUploadToken();
    if (!token) return Promise.resolve(null);
    return Axios.get(`${BASE}/me`, { headers: authHeader(token), timeout: 10000 })
        .then((r) => {
            // Refresh the name but NOT the activity stamp — whether the phone has been sitting idle is exactly
            // what the caller is about to decide, and touching it here would always say "still active".
            write(ADMIN_KEY, r.data.admin ? JSON.stringify(r.data.admin) : null);
            return { admin: r.data.admin, admins: r.data.admins || [], limited: Boolean(r.data.limited) };
        })
        .catch((err) => {
            // null = definitely no session (ask for the password). undefined = could not tell — a timeout, a 500,
            // no signal in the back office. Those must NOT cost the password, and must not drop the page's state.
            if (err?.response?.status === 401) { clearUploadSession(); return null; }
            return undefined;
        });
};
